import { type JSX, useState } from "react";

import { ManageSubscription } from "features/subscriptions/components";
import useManageSubscriptionSteps from "features/subscriptions/hooks/useManageSubscriptionSteps";
import type { MixpanelSubscriptionSource } from "features/subscriptions/types";

import { DialogContent } from "common/components/ui/Dialog";
import { DrawerContent } from "common/components/ui/Drawer";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useWorkspaceDetails from "common/datahooks/useWorkspaceDetails";
import { getOutreachLicenseCount } from "common/helpers/workspaceLicenseCounts";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import SeatPurchase from "./SeatPurchase";

interface NotEnoughSeatsProps {
  noSeatAccountIds: string[];
  mixpanelSource: Exclude<MixpanelSubscriptionSource, "billing_page">;
}

export default function NotEnoughSeats({
  noSeatAccountIds,
  mixpanelSource,
}: NotEnoughSeatsProps) {
  const isDesktop = useTwBreakpoint("lg");
  const selectedWorkspace = useSelectedWorkspaceContext();
  const { id: workspaceId } = selectedWorkspace;
  const { workspace } = useWorkspaceDetails(workspaceId);
  const outreachSeatCount = getOutreachLicenseCount(selectedWorkspace);

  const initialState = workspace?.subscription
    ? {
        subscription: workspace.subscription,
        seats: getOutreachLicenseCount(workspace) ?? outreachSeatCount ?? 1,
      }
    : "user";

  const { manageSubscriptionStep, goToManageSubscriptionStep, dialogSize } =
    useManageSubscriptionSteps(initialState, false);

  const [currentStep, setCurrentStep] = useState<
    "noSeats" | "manageSubscription"
  >("noSeats");

  const Content = isDesktop ? DialogContent : DrawerContent;

  let mainComponent: JSX.Element;

  if (currentStep === "noSeats") {
    mainComponent = (
      <SeatPurchase
        additionalSeatsCount={noSeatAccountIds.length}
        goToManageSubscription={() => setCurrentStep("manageSubscription")}
      />
    );
  } else {
    mainComponent = (
      <ManageSubscription
        initialState={initialState}
        step={manageSubscriptionStep}
        goToStep={goToManageSubscriptionStep}
        mixpanelSource={mixpanelSource}
      />
    );
  }

  return (
    <Content size={currentStep === "noSeats" ? "md" : dialogSize} key="manage">
      {mainComponent}
    </Content>
  );
}
